import { Controller } from 'stimulus';
import { FetchRequest } from '@rails/request.js';
import { show as showToast } from '@/js/application_controllers/ui/toast_controller';

const GENERIC_ERROR = 'Unable to rename. Please try again.';

export default class extends Controller {
  static targets = ['editor', 'input', 'save', 'error', 'title', 'trigger'];
  static values = { url: String, datasetKey: String, toastId: String };

  edit() {
    this.inputTarget.value = this.titleTarget.title;
    this.errorTarget.hidden = true;
    this.inputTarget.removeAttribute('aria-invalid');
    this.#toggleEditing(true);
    this.inputTarget.focus();
    this.inputTarget.select();
  }

  cancel() {
    if (this.#canCancel()) {
      this.#toggleEditing(false);
      this.triggerTarget.focus();
    }
  }

  cancelOnOutsidePointer(event) {
    if (this.#canCancel() && !this.element.contains(event.target)) {
      this.#toggleEditing(false);
    }
  }

  // Safari does not focus buttons on click, so a null relatedTarget may be a press on Save.
  cancelOnFocusOut(event) {
    const next = event.relatedTarget;
    if (this.#canCancel() && next && !this.editorTarget.contains(next)) {
      this.#toggleEditing(false);
    }
  }

  stopPropagation(event) {
    event.stopPropagation();
  }

  keydown(event) {
    event.stopPropagation();
    if (event.isComposing) {
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      this.save();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.cancel();
    }
  }

  async save() {
    if (this.saving) {
      return;
    }

    const name = this.inputTarget.value.trim();
    if (!name) {
      this.#showError("Name can't be blank.");
      return;
    }

    this.saving = true;
    this.inputTarget.readOnly = true;
    this.saveTarget.disabled = true;
    this.errorTarget.hidden = true;

    try {
      const response = await new FetchRequest('post', this.urlValue, {
        body: { name },
        responseKind: 'json',
      }).perform();
      const result = await response.json;
      if (!response.ok) {
        this.#showError(result.error || GENERIC_ERROR);
        return;
      }

      this.#updateTitle(result.name);
      this.#toggleEditing(false);
      this.triggerTarget.focus();
      this.#showRenamedToast(result.name);
      this.dispatch('renamed', { detail: { name: result.name } });
    } catch {
      this.#showError(GENERIC_ERROR);
    } finally {
      this.saving = false;
      this.inputTarget.readOnly = false;
      this.saveTarget.disabled = false;
      if (!this.editorTarget.hidden) {
        this.inputTarget.focus();
      }
    }
  }

  #canCancel() {
    return !this.saving && !this.editorTarget.hidden;
  }

  #toggleEditing(editing) {
    this.editorTarget.hidden = !editing;
    this.inputTarget.disabled = !editing;
    this.triggerTarget.disabled = editing;
    this.element.classList.toggle('is-renaming', editing);
    if (this.element.hasAttribute('draggable')) {
      this.element.draggable = !editing;
    }
  }

  #showError(message) {
    this.errorTarget.textContent = message;
    this.errorTarget.hidden = false;
    this.inputTarget.setAttribute('aria-invalid', 'true');
    this.inputTarget.focus();
  }

  // The page renders the toast template once; its name slot is refilled before each show clones it.
  #showRenamedToast(name) {
    const template = this.hasToastIdValue && document.getElementById(this.toastIdValue);
    if (!template) {
      return;
    }

    template.content.querySelector('[data-role="renamed-name"]').textContent = name;
    showToast(template);
  }

  #updateTitle(name) {
    this.titleTarget.title = name;
    this.titleTarget.querySelector('span').textContent = name;
    if (this.hasDatasetKeyValue) {
      this.element.dataset[this.datasetKeyValue] = name;
    }
  }
}
